/*
 * Copyright © 2025 Gavin Sawyer. All rights reserved.
 */

import { inject, Injectable, type Signal }                                                                              from "@angular/core";
import { toSignal }                                                                                                     from "@angular/core/rxjs-interop";
import { type FirebaseError }                                                                                           from "@angular/fire/app";
import { Auth, EmailAuthProvider, linkWithCredential, reauthenticateWithCredential, unlink, updatePassword, type User } from "@angular/fire/auth";
import { doc, docData, type DocumentReference, Firestore, setDoc }                                                      from "@angular/fire/firestore";
import { Functions }                                                                                                    from "@angular/fire/functions";
import { getAuthErrorMessage }                                                                                          from "@bowstring/firebase-interop";
import { type AccountDocument }                                                                                         from "@bowstring/interfaces";
import { type FirebaseWebAuthnError, linkWithPasskey, unlinkPasskey }                                                   from "@firebase-web-authn/browser";
import { catchError, map, Observable, of, switchMap }                                                                   from "rxjs";
import { AuthenticationService }                                                                                        from "./AuthenticationService";
import { ErrorsService }                                                                                                from "./ErrorsService";
import { RxSsrService }                                                                                                 from "./RxSsrService";


@Injectable({ providedIn: "root" })
export class AccountService {

  private readonly auth: Auth                                   = inject<Auth>(Auth);
  private readonly authenticationService: AuthenticationService = inject<AuthenticationService>(AuthenticationService);
  private readonly errorsService: ErrorsService                 = inject<ErrorsService>(ErrorsService);
  private readonly firestore: Firestore                         = inject<Firestore>(Firestore);
  private readonly functions: Functions                         = inject<Functions>(Functions);
  private readonly rxSsrService: RxSsrService                   = inject<RxSsrService>(RxSsrService);

  public readonly accountDocument$: Signal<AccountDocument | undefined>                                              = toSignal<AccountDocument | undefined>(
    this.authenticationService.userObservable.pipe<AccountDocument | undefined>(
      this.rxSsrService.wrap<User, AccountDocument | undefined>(
        switchMap<User, Observable<AccountDocument | undefined>>(
          ({ uid: userId }: User): Observable<AccountDocument | undefined> => docData<AccountDocument>(
            doc(
              this.firestore,
              `/accounts/${ userId }`,
            ) as DocumentReference<AccountDocument, AccountDocument>,
          ).pipe<AccountDocument | undefined>(
            catchError<AccountDocument | undefined, Observable<undefined>>(
              (): Observable<undefined> => of<undefined>(undefined),
            ),
          ),
        ),
        "019682d4-6926-7119-93f4-cb67e24450fb",
      ),
    ),
  );
  public readonly accountDocumentReference$: Signal<DocumentReference<AccountDocument, AccountDocument> | undefined> = toSignal<DocumentReference<AccountDocument, AccountDocument> | undefined>(
    this.authenticationService.userObservable.pipe<DocumentReference<AccountDocument, AccountDocument> | undefined>(
      map<User, DocumentReference<AccountDocument, AccountDocument>>(
        ({ uid: userId }: User): DocumentReference<AccountDocument, AccountDocument> => doc(
          this.firestore,
          `/accounts/${ userId }`,
        ) as DocumentReference<AccountDocument, AccountDocument>,
      ),
    ),
  );

  public async linkWithEmailAndPasswordCredential(
    email: string,
    password: string,
  ): Promise<void> {
    const user: User | undefined = this.authenticationService.user$();

    if (user)
      return linkWithCredential(
        user,
        EmailAuthProvider.credential(
          email,
          password,
        ),
      ).then<void, never>(
        (): Promise<void> => setDoc<AccountDocument, AccountDocument>(
          doc(
            this.firestore,
            `/accounts/${ user.uid }`,
          ) as DocumentReference<AccountDocument, AccountDocument>,
          { security: { password: true } },
          { merge: true },
        ).catch<never>(
          (error: Error): never => {
            console.error("Something went wrong.");

            throw error;
          },
        ),
        (firebaseError: FirebaseError): never => {
          this.errorsService.createError(getAuthErrorMessage(firebaseError));

          throw firebaseError;
        },
      );
  }
  public async linkWithPasskey(): Promise<void> {
    const email: string | undefined  = this.accountDocument$()?.email;
    const userId: string | undefined = this.authenticationService.user$()?.uid;

    if (email && userId)
      return linkWithPasskey(
        this.auth,
        this.functions,
        email,
      ).then<void, never>(
        (): Promise<void> => setDoc<AccountDocument, AccountDocument>(
          doc(
            this.firestore,
            `/accounts/${ userId }`,
          ) as DocumentReference<AccountDocument, AccountDocument>,
          { security: { passkey: true } },
          { merge: true },
        ).catch<never>(
          (error: Error): never => {
            console.error("Something went wrong.");

            throw error;
          },
        ),
        (firebaseWebAuthnError: FirebaseWebAuthnError): never => {
          this.errorsService.createError(firebaseWebAuthnError.code === "firebaseWebAuthn/no-op" ? "You already have a passkey." : firebaseWebAuthnError.message);

          throw firebaseWebAuthnError;
        },
      );
  }
  public async linkWithPasskeyBackup(): Promise<void> {
    const email: string | undefined  = this.accountDocument$()?.email;
    const userId: string | undefined = this.authenticationService.user$()?.uid;

    if (email && userId)
      return linkWithPasskey(
        this.auth,
        this.functions,
        `${ email } (Backup)`,
        "second",
      ).then<void, never>(
        (): Promise<void> => setDoc<AccountDocument, AccountDocument>(
          doc(
            this.firestore,
            `/accounts/${ userId }`,
          ) as DocumentReference<AccountDocument, AccountDocument>,
          { security: { passkeyBackup: true } },
          { merge: true },
        ).catch<never>(
          (error: Error): never => {
            console.error("Something went wrong.");

            throw error;
          },
        ),
        (firebaseWebAuthnError: FirebaseWebAuthnError): never => {
          this.errorsService.createError(firebaseWebAuthnError.code === "firebaseWebAuthn/no-op" ? "You already have a passkey backup." : firebaseWebAuthnError.message);

          throw firebaseWebAuthnError;
        },
      );
  }
  public async unlinkPasskey(): Promise<void> {
    const userId: string | undefined = this.authenticationService.user$()?.uid;

    if (userId)
      return unlinkPasskey(
        this.auth,
        this.functions,
      ).then<void, never>(
        (): Promise<void> => setDoc<AccountDocument, AccountDocument>(
          doc(
            this.firestore,
            `/accounts/${ userId }`,
          ) as DocumentReference<AccountDocument, AccountDocument>,
          { security: { passkey: false } },
          { merge: true },
        ).catch<never>(
          (error: Error): never => {
            console.error("Something went wrong.");

            throw error;
          },
        ),
        (firebaseWebAuthnError: FirebaseWebAuthnError): never => {
          this.errorsService.createError(firebaseWebAuthnError.code === "firebaseWebAuthn/no-op" ? "You do not have a passkey." : firebaseWebAuthnError.message);

          throw firebaseWebAuthnError;
        },
      );
  }
  public async unlinkPasskeyBackup(): Promise<void> {
    const userId: string | undefined = this.authenticationService.user$()?.uid;

    if (userId)
      return unlinkPasskey(
        this.auth,
        this.functions,
        "second",
      ).then<void, never>(
        (): Promise<void> => setDoc<AccountDocument, AccountDocument>(
          doc(
            this.firestore,
            `/accounts/${ userId }`,
          ) as DocumentReference<AccountDocument, AccountDocument>,
          { security: { passkeyBackup: false } },
          { merge: true },
        ).catch<never>(
          (error: Error): never => {
            console.error("Something went wrong.");

            throw error;
          },
        ),
        (firebaseWebAuthnError: FirebaseWebAuthnError): never => {
          this.errorsService.createError(firebaseWebAuthnError.code === "firebaseWebAuthn/no-op" ? "You do not have a passkey." : firebaseWebAuthnError.message);

          throw firebaseWebAuthnError;
        },
      );
  }
  public async unlinkPassword(): Promise<void> {
    const user: User | undefined = this.authenticationService.user$();

    if (user)
      return unlink(
        user,
        "password",
      ).then<void, never>(
        (): Promise<void> => setDoc<AccountDocument, AccountDocument>(
          doc(
            this.firestore,
            `/accounts/${ user.uid }`,
          ) as DocumentReference<AccountDocument, AccountDocument>,
          { security: { password: false } },
          { merge: true },
        ).catch<never>(
          (error: Error): never => {
            console.error("Something went wrong.");

            throw error;
          },
        ),
        (firebaseError: FirebaseError): never => {
          this.errorsService.createError(getAuthErrorMessage(firebaseError));

          throw firebaseError;
        },
      );
  }
  public async updateEmailAndPasswordCredential(
    passwordCurrent: string,
    passwordNew: string,
  ): Promise<void> {
    const email: string | undefined = this.accountDocument$()?.email;
    const user: User | undefined    = this.authenticationService.user$();

    if (email && user)
      return reauthenticateWithCredential(
        user,
        EmailAuthProvider.credential(
          email,
          passwordCurrent,
        ),
      ).then<void, never>(
        (): Promise<void> => updatePassword(
          user,
          passwordNew,
        ).catch<never>(
          (firebaseError: FirebaseError): never => {
            this.errorsService.createError(getAuthErrorMessage(firebaseError));

            throw firebaseError;
          },
        ),
        (firebaseError: FirebaseError): never => {
          this.errorsService.createError(getAuthErrorMessage(firebaseError));

          throw firebaseError;
        },
      );
  }

}
